import {
  HOTEL_AUTOCOMPLETE_BUCKET,
  HOTEL_AUTOCOMPLETE_ITEM_TYPE,
  HOTEL_AUTOCOMPLETE_QUERY_TYPE,
  HOTEL_AUTOCOMPLETE_SECTION_ORDER,
  HOTEL_SEARCH_INTENT_ACTION,
} from '../constants/hotelSearch.constants';
import { pickLocationId } from './hotelSearchParams';

export function isCitySuggestion(item) {
  return item?.bucket === HOTEL_AUTOCOMPLETE_BUCKET.CITY
    || item?.type === HOTEL_AUTOCOMPLETE_ITEM_TYPE.CITY;
}

export function isHotelSuggestion(item) {
  return item?.bucket === HOTEL_AUTOCOMPLETE_BUCKET.HOTEL
    || item?.type === HOTEL_AUTOCOMPLETE_ITEM_TYPE.HOTEL;
}

/**
 * Google autocomplete currently (or previously) dumped every suggestion into
 * `locations`. The homepage dropdown still has city / hotel / location sections,
 * so re-home items by bucket/type and never drop preloaded locations.
 */
export function partitionHotelAutocompleteResults(result, { includeHotels = true } = {}) {
  if (!result || result.success === false) {
    return {
      cities: [],
      hotels: [],
      locations: [],
      queryType: HOTEL_AUTOCOMPLETE_QUERY_TYPE.CITY,
    };
  }

  const cities = [...(result.cities || [])];
  const hotels = includeHotels ? [...(result.hotels || [])] : [];
  const locations = [];
  const seenCityIds = new Set(cities.map((item) => item.cityId).filter(Boolean));
  const seenHotelCodes = new Set(hotels.map((item) => item.hotelCode).filter(Boolean));

  for (const item of result.locations || []) {
    if (isCitySuggestion(item)) {
      if (item.cityId && seenCityIds.has(item.cityId)) continue;
      if (item.cityId) seenCityIds.add(item.cityId);
      cities.push(item);
      continue;
    }
    if (isHotelSuggestion(item)) {
      if (!includeHotels) continue;
      if (item.hotelCode && seenHotelCodes.has(item.hotelCode)) continue;
      if (item.hotelCode) seenHotelCodes.add(item.hotelCode);
      hotels.push(item);
      continue;
    }
    locations.push(item);
  }

  return {
    cities,
    hotels,
    locations,
    queryType: result.queryType || HOTEL_AUTOCOMPLETE_QUERY_TYPE.CITY,
  };
}

export function getHotelAutocompleteSectionOrder(queryType) {
  return HOTEL_AUTOCOMPLETE_SECTION_ORDER[queryType]
    || HOTEL_AUTOCOMPLETE_SECTION_ORDER[HOTEL_AUTOCOMPLETE_QUERY_TYPE.CITY];
}

/**
 * Google ranks suggestions in `locations` (cities/hotels currently live there too).
 * Fall back to the split arrays for older TBO-style payloads.
 */
export function rankedHotelAutocompleteSuggestions(result) {
  if (!result) return [];
  const locations = result.locations || [];
  if (locations.length > 0) return locations;
  return [...(result.cities || []), ...(result.hotels || [])];
}

export function classifyHotelAutocompleteSuggestion(item) {
  if (isCitySuggestion(item)) return HOTEL_AUTOCOMPLETE_ITEM_TYPE.CITY;
  if (isHotelSuggestion(item)) return HOTEL_AUTOCOMPLETE_ITEM_TYPE.HOTEL;
  return HOTEL_AUTOCOMPLETE_BUCKET.LOCATION;
}

export function pickFirstHotelAutocompleteSuggestion(result) {
  return rankedHotelAutocompleteSuggestions(result)[0] || null;
}

export function toHotelSearchAutoSelect(item) {
  if (!item) return null;

  const type = classifyHotelAutocompleteSuggestion(item);
  if (type === HOTEL_AUTOCOMPLETE_ITEM_TYPE.CITY) {
    return {
      action: HOTEL_SEARCH_INTENT_ACTION.AUTO_SELECT,
      type: 'city',
      data: {
        cityId: item.cityId,
        cityName: item.cityName,
        countryCode: item.countryCode,
        countryName: item.countryName,
        displayName: item.label || item.displayName || item.cityName,
      },
    };
  }

  if (type === HOTEL_AUTOCOMPLETE_ITEM_TYPE.HOTEL) {
    return {
      action: HOTEL_SEARCH_INTENT_ACTION.AUTO_SELECT,
      type: 'hotel',
      data: {
        hotelCode: item.hotelCode,
        hotelName: item.hotelName,
        cityId: item.cityId,
        cityName: item.cityName,
        countryCode: item.countryCode,
        countryName: item.countryName,
        starRating: item.starRating,
        displayName: item.hotelName,
      },
    };
  }

  return {
    action: HOTEL_SEARCH_INTENT_ACTION.AUTO_SELECT,
    type: 'location',
    data: {
      locationId: pickLocationId(item),
      locationType: item.locationType,
      cityId: item.cityId || item.parentDestinationId,
      cityName: item.cityName || item.parentCityName,
      countryCode: item.countryCode,
      displayName: item.displayName || item.label,
    },
  };
}

export function toHotelLocationSelectInput(item) {
  if (!item) return null;
  const type = classifyHotelAutocompleteSuggestion(item);

  if (type === HOTEL_AUTOCOMPLETE_ITEM_TYPE.CITY) {
    return {
      ...item,
      type: 'city',
      id: item.cityId,
      city: item.cityName,
      country: item.countryName,
    };
  }

  if (type === HOTEL_AUTOCOMPLETE_ITEM_TYPE.HOTEL) {
    return {
      ...item,
      type: 'hotel',
      id: item.hotelCode,
      name: item.hotelName,
      city: item.cityName,
      country: item.countryName,
    };
  }

  return {
    ...item,
    type: 'location',
    id: pickLocationId(item),
    name: item.displayName,
    city: item.cityName,
    country: item.countryName,
  };
}
